import { Controller } from "@hotwired/stimulus"

// Artist credits as repeatable "artist + role" rows.
//
// Deliberately not a subclass of artist_search_base: that base assumes one
// search box per controller and one role per controller, which is the album's
// role-bucket model, not this one.
export default class extends Controller {
  static targets = ["rows", "row", "empty", "search", "searchInput", "results", "status"]
  static values = {
    searchUrl: { type: String, default: "/albums/search_artists" },
    organizationId: Number,
    roleOptions: Array,
    placeholder: String,
    removeLabel: String,
    persistUrl: String,
    removeConfirm: String
  }

  addRow() {
    this.searchTarget.classList.remove("hidden")
    this.searchInputTarget.value = ""
    this.searchInputTarget.focus()
    this.loadArtists("")
  }

  async search() {
    const query = this.searchInputTarget.value.trim()
    if (query.length > 0 && query.length < 2) return

    this.loadArtists(query)
  }

  async loadArtists(query) {
    const params = new URLSearchParams({ organization_id: this.organizationIdValue })
    if (query) {
      params.append("query", query)
    } else {
      params.append("initial", "true")
    }

    try {
      const response = await fetch(`${this.searchUrlValue}?${params}`, {
        headers: { "Accept": "application/json" }
      })
      if (!response.ok) return

      this.renderResults(await response.json())
    } catch (error) {
      this.resultsTarget.classList.add("hidden")
    }
  }

  renderResults(artists) {
    this.resultsTarget.innerHTML = ""

    artists.slice(0, 20).forEach(artist => {
      const option = document.createElement("button")
      option.type = "button"
      option.className = "w-full text-left px-3 py-2 text-sm text-gray-900 hover:bg-gray-100 cursor-pointer"
      option.textContent = artist.name
      option.addEventListener("click", () => this.selectArtist(artist))
      this.resultsTarget.appendChild(option)
    })

    if (artists.length === 0) {
      const empty = document.createElement("p")
      empty.className = "px-3 py-2 text-sm text-gray-500"
      empty.textContent = this.t("artists.no_artists_found")
      this.resultsTarget.appendChild(empty)
    }

    // Same escape hatch the album credits offer: if the artist is not on the
    // roster yet, create them without leaving the page.
    const addNew = document.createElement("button")
    addNew.type = "button"
    addNew.className = "w-full border-t border-gray-200 px-3 py-2 text-sm text-center font-medium text-gray-900 hover:text-gray-600 hover:bg-gray-50 cursor-pointer"
    addNew.textContent = this.t("artists.add_new_artist")
    addNew.addEventListener("click", () => this.showAddArtistModal())
    this.resultsTarget.appendChild(addNew)

    this.resultsTarget.classList.remove("hidden")
  }

  // Hands off to the shared add-artist modal. It calls us back through
  // add_artist_form_controller#addArtistToDropdown, which looks this controller
  // up by the calling-controller tag set here.
  showAddArtistModal() {
    this.resultsTarget.classList.add("hidden")
    this.searchInputTarget.value = ""

    const modal = document.getElementById("add-artist-modal")
    if (!modal) return

    modal.dataset.callingController = "music_video_credits"

    const contributorFlag = modal.querySelector('[data-add-artist-form-target="contributorFlag"]')
    if (contributorFlag) contributorFlag.value = "false"

    modal.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
  }

  selectArtist(artist) {
    if (this.creditedArtistIds().includes(String(artist.id))) return

    this.rowsTarget.insertAdjacentHTML("beforeend", this.rowMarkup(artist))
    this.resultsTarget.classList.add("hidden")
    this.searchTarget.classList.add("hidden")
    this.updateEmptyState()
    this.persist()
  }

  removeRow(event) {
    // Dropping a credit writes through immediately, so ask first.
    if (this.hasRemoveConfirmValue && !window.confirm(this.removeConfirmValue)) return

    const row = event.currentTarget.closest("[data-video-credits-target='row']")
    if (row) row.remove()
    this.updateEmptyState()
    this.persist()
  }

  // Credits save themselves - adding, removing or re-roling one is committed
  // straight away rather than waiting for the form's Save and Continue.
  async persist() {
    if (!this.hasPersistUrlValue) return

    const credits = Array.from(this.rowsTarget.querySelectorAll("[data-video-credits-target='row']")).map(row => ({
      artist_id: row.querySelector('input[name="music_video[credits][][artist_id]"]')?.value,
      role: row.querySelector('select[name="music_video[credits][][role]"]')?.value
    })).filter(credit => credit.artist_id && credit.role)

    try {
      const response = await fetch(this.persistUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ music_video: { credits } })
      })

      const payload = await response.json()

      // Nothing is announced on success - the row appearing or disappearing is
      // the feedback. A failure still has to be said out loud, since the change
      // is already on screen but not in the database.
      if (!response.ok || !payload.success) {
        this.showStatus(payload.error || this.t("music_videos.edit.credits_failed"), "text-red-600")
      } else {
        this.hideStatus()
      }
    } catch (error) {
      this.showStatus(this.t("music_videos.edit.credits_failed"), "text-red-600")
    }
  }

  showStatus(message, colorClass) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = message
    this.statusTarget.className = `text-xs ${colorClass}`
  }

  hideStatus() {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = ""
    this.statusTarget.classList.add("hidden")
  }

  t(key) {
    if (window.I18n && typeof window.I18n.t === "function") return window.I18n.t(key)
    return key
  }

  creditedArtistIds() {
    return Array.from(this.element.querySelectorAll('input[name="music_video[credits][][artist_id]"]'))
      .map(input => input.value)
  }

  updateEmptyState() {
    const any = this.rowsTarget.children.length > 0
    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("hidden", any)
    this.rowsTarget.classList.toggle("hidden", !any)
  }

  rowMarkup(artist) {
    const options = this.roleOptionsValue
      .map(option => `<option value="${option.value}">${this.escape(option.label)}</option>`)
      .join("")

    return `
      <div class="flex flex-col gap-3 p-3 sm:flex-row sm:items-center" data-video-credits-target="row">
        <div class="flex items-center gap-3 min-w-0 flex-1">
          <span class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-900">
            ${this.escape(this.initials(artist.name))}
          </span>
          <span class="text-sm font-medium text-gray-900 truncate" title="${this.escape(artist.name)}">
            ${this.escape(artist.name)}
          </span>
          <input type="hidden" name="music_video[credits][][artist_id]" value="${artist.id}">
        </div>

        <div class="flex items-center gap-2 sm:flex-shrink-0">
          <div class="relative flex-1 sm:w-52">
            <select name="music_video[credits][][role]"
                    data-action="change->video-credits#persist"
                    class="appearance-none w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900">
              ${options}
            </select>
            ${this.chevron()}
          </div>

          <button type="button" data-action="click->video-credits#removeRow"
                  title="${this.escape(this.removeLabelValue)}"
                  aria-label="${this.escape(this.removeLabelValue)}"
                  class="p-2 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors cursor-pointer flex-shrink-0">
            <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    `
  }

  // The house dropdown arrow. Server-rendered rows get it from
  // shared/_select_wrapper; rows built here draw the same icon so the two
  // kinds of row are indistinguishable.
  chevron() {
    return `
      <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-700">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 16 16" fill="none">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M1.64601 4.64592C1.69245 4.59935 1.74763 4.56241 1.80838 4.5372C1.86912 4.512 1.93424 4.49902 2.00001 4.49902C2.06578 4.49902 2.1309 4.512 2.19164 4.5372C2.25239 4.56241 2.30756 4.59935 2.35401 4.64592L8.00001 10.2929L13.646 4.64592C13.6925 4.59943 13.7477 4.56255 13.8084 4.53739C13.8692 4.51223 13.9343 4.49929 14 4.49929C14.0658 4.49929 14.1309 4.51223 14.1916 4.53739C14.2523 4.56255 14.3075 4.59943 14.354 4.64592C14.4005 4.6924 14.4374 4.74759 14.4625 4.80833C14.4877 4.86907 14.5006 4.93417 14.5006 4.99992C14.5006 5.06566 14.4877 5.13076 14.4625 5.1915C14.4374 5.25224 14.4005 5.30743 14.354 5.35392L8.35401 11.3539C8.30756 11.4005 8.25239 11.4374 8.19164 11.4626C8.1309 11.4878 8.06578 11.5008 8.00001 11.5008C7.93424 11.5008 7.86912 11.4878 7.80838 11.4626C7.74763 11.4374 7.69245 11.4005 7.64601 11.3539L1.64601 5.35392C1.59945 5.30747 1.5625 5.2523 1.5373 5.19155C1.51209 5.13081 1.49911 5.06568 1.49911 4.99992C1.49911 4.93415 1.51209 4.86903 1.5373 4.80828C1.5625 4.74754 1.59945 4.69236 1.64601 4.64592Z" fill="currentColor"/>
        </svg>
      </div>
    `
  }

  initials(name) {
    return String(name || "")
      .split(/\s+/)
      .slice(0, 2)
      .map(part => part[0] || "")
      .join("")
      .toUpperCase()
  }

  escape(value) {
    const div = document.createElement("div")
    div.textContent = value == null ? "" : value
    return div.innerHTML
  }
}
